import { definePreset, palette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import {
  brandCtaPrimaryHex,
  gpxButtonThemeColors,
  gpxGray,
  gpxPrimeSurfaceDark,
  gpxPrimeSurfaceLight,
} from '../tokens/gpx-brand-colors.js';

const B = gpxButtonThemeColors;

/**
 * Single GPX extension of PrimeVue’s Aura preset. All apps that use PrimeVue must
 * use this preset (via `gpxPrimeVueTheme`) so tokens and component overrides stay aligned.
 *
 * `semantic.primary` is seeded from `brandCtaPrimaryHex` (same as UI **btn-accent**).
 * **Button** geometry (padding, radius, sizes) uses Aura defaults; only colors are overridden via
 * `gpxButtonThemeColors` (from `gpx-brand-colors.js`, aligned with `:root --btn-*`) for filled /
 * outlined / dark text contrast. **Surface** neutrals use GPX `gpx_gray` / `gpx_black` via
 * `gpxPrimeSurfaceLight` / `gpxPrimeSurfaceDark`.
 *
 * Outlined **danger** hover label color: `@primeuix/styles` reuses `color` for hover (no separate
 * token) — see `gpx-primevue-overrides.css` + `docs/primevue-shared-theme.md`.
 */
export const gpxAuraPreset = definePreset(Aura, {
  semantic: {
    primary: palette(brandCtaPrimaryHex),
    colorScheme: {
      light: {
        surface: gpxPrimeSurfaceLight,
      },
      dark: {
        surface: gpxPrimeSurfaceDark,
      },
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              background: B.accent,
              hoverBackground: B.accentHover,
              activeBackground: B.accentHover,
              borderColor: B.accent,
              hoverBorderColor: B.accentHover,
              activeBorderColor: B.accentHover,
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
              focusRing: {
                color: B.accent,
                shadow: 'none',
              },
            },
            secondary: {
              background: B.secondaryBg,
              hoverBackground: B.secondaryHoverBg,
              activeBackground: B.secondaryHoverBg,
              borderColor: B.secondaryBg,
              hoverBorderColor: B.secondaryHoverBg,
              activeBorderColor: B.secondaryHoverBg,
              color: B.secondaryText,
              hoverColor: B.secondaryText,
              activeColor: B.secondaryText,
              focusRing: {
                color: B.secondaryBg,
                shadow: 'none',
              },
            },
          },
          outlined: {
            danger: {
              borderColor: B.accent,
              color: B.accentText,
              background: '#ffffff',
              hoverBackground: B.accent,
              activeBackground: B.accentHover,
            },
            secondary: {
              borderColor: B.tertiaryBorder,
              color: B.tertiaryText,
              background: '#ffffff',
              hoverBackground: B.tertiaryHoverBg,
              activeBackground: '#e8eaed',
              hoverBorderColor: B.tertiaryBorder,
              activeBorderColor: B.tertiaryBorder,
              hoverColor: B.tertiaryText,
              activeColor: B.tertiaryText,
            },
          },
        },
        /**
         * With a `.dark` ancestor, `severity="contrast"` + `text` gets light label colors.
         * Hover/active backgrounds are set here; hover label color is not a separate token — use
         * `class="hover:!text-gpx_black-100"` (or similar) on the button to mirror UI `ghost` when hover fill is white.
         */
        dark: {
          text: {
            contrast: {
              color: '#ffffff',
              hoverBackground: '#ffffff',
              activeBackground: '#f4f5f6',
            },
          },
        },
      },
    },
    /**
     * ProgressSpinner: `@primeuix/styles` animates stroke through four tokens (`colorOne`…`colorFour`).
     * Aura defaults use four different hues (rainbow). GPX sets all to CTA so the animation keeps
     * a single brand colour (dash motion only).
     */
    progressspinner: {
      colorScheme: {
        light: {
          root: {
            colorOne: brandCtaPrimaryHex,
            colorTwo: brandCtaPrimaryHex,
            colorThree: brandCtaPrimaryHex,
            colorFour: brandCtaPrimaryHex,
          },
        },
        dark: {
          root: {
            colorOne: brandCtaPrimaryHex,
            colorTwo: brandCtaPrimaryHex,
            colorThree: brandCtaPrimaryHex,
            colorFour: brandCtaPrimaryHex,
          },
        },
      },
    },
    /**
     * Slider: thicker track (~labs), neutral track fill, range + thumb use primary (brand CTA).
     * Handle is a single primary disc (::before); outer handle box is transparent so alignment matches PrimeVue layout.
     * @see @primeuix/styles/dist/slider — `.p-slider-handle::before` uses `handle.content.*` tokens.
     */
    slider: {
      track: {
        size: '6px',
        background: gpxGray[800],
        borderRadius: '9999px',
      },
      range: {
        background: '{primary.color}',
        borderRadius: '9999px',
      },
      handle: {
        width: '16px',
        height: '16px',
        background: 'transparent',
        hoverBackground: 'transparent',
        borderRadius: '50%',
        content: {
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: '{primary.color}',
          hoverBackground: '{primary.color}',
          shadow: '0 0 0 2px #ffffff, 0 1px 3px rgba(0, 0, 0, 0.2)',
        },
        focusRing: {
          width: '{focus.ring.width}',
          style: '{focus.ring.style}',
          color: '{focus.ring.color}',
          offset: '{focus.ring.offset}',
          shadow: '{focus.ring.shadow}',
        },
      },
      colorScheme: {
        light: {
          handle: {
            content: {
              background: '{primary.color}',
              hoverBackground: '{primary.color}',
            },
          },
        },
        dark: {
          handle: {
            content: {
              background: '{primary.color}',
              hoverBackground: '{primary.color}',
            },
          },
        },
      },
    },
    /**
     * Tooltip (v-tooltip directive): tighter than Aura default padding/gutter so help text feels less bulky.
     */
    tooltip: {
      root: {
        padding: '0.25rem 0.5rem',
        gutter: '0.125rem',
      },
    },
    /**
     * DatePicker: slightly tighter overlay than Aura default so shipment stop times feel less bulky.
     */
    datepicker: {
      panel: {
        padding: '0.5rem',
      },
      date: {
        width: '1.75rem',
        height: '1.75rem',
        padding: '0.125rem',
      },
      timePicker: {
        padding: '0.375rem 0 0 0',
        gap: '0.375rem',
      },
    },
    /**
     * ConfirmDialog: smaller icon + tighter content gap so the modal matches dense PrimeVue demos.
     * (Footer buttons: see `gpx-primevue-overrides.css` — base `.p-button` is primary-filled.)
     */
    confirmdialog: {
      icon: {
        size: '1.25rem',
      },
      content: {
        gap: '0.75rem',
      },
    },
    /**
     * RadioButton `size="small"`: Aura uses `icon.sm.size` = `0.5rem` (can be subpixel vs flex + `scale()`). Odd pixel
     * size plus flex + `scale()` can look slightly off-center. `8px` keeps the dot even-sized without
     * changing Prime’s layout (still flex-centered).
     */
    radiobutton: {
      icon: {
        sm: {
          size: '8px',
        },
      },
    },
    /**
     * DataTable: lighter, roomier header cells. `textTransform` is not a design token —
     * uppercase is handled in `gpx-primevue-overrides.css`.
     */
    datatable: {
      headerCell: {
        padding: '1rem 1rem',
        sm: {
          padding: '0.625rem 0.5rem',
        },
        lg: {
          padding: '1.25rem 1.25rem',
        },
      },
      columnTitle: {
        fontWeight: '300',
      },
    },
  },
});

/**
 * Value for `theme` in `app.use(PrimeVue, { theme: gpxPrimeVueTheme, … })`.
 */
export const gpxPrimeVueTheme = {
  preset: gpxAuraPreset,
  options: {
    darkModeSelector: '.dark',
  },
};
